/**
 * Shared formatting for plan limits: -1 means unlimited, and monthly scans are capped by the platform ceiling.
 */
import type { TierConfig } from '@/types/tier';

export const PLAN_ORDER = ['free', 'starter', 'professional', 'enterprise'];
export const PLAN_NAMES: Record<string, string> = { free: 'Free', starter: 'Starter', professional: 'Professional', enterprise: 'Enterprise' };
export const CONNECTION_NAMES: Record<string, string> = { long_poll: 'Long Poll', websocket: 'WebSocket' };

export const count = (value: number) => (value === -1 ? 'Unlimited' : value.toLocaleString('en-US'));
export const hours = (value: number) => `${value} hour${value === 1 ? '' : 's'}`;

/** Scans per month actually allowed: the plan setting, never above the platform ceiling (-1 = none). */
export function effectiveMonthly(plan: Pick<TierConfig, 'max_scans_per_month' | 'platform'>) {
  const setting = plan.max_scans_per_month, ceiling = plan.platform?.monthly_ceiling ?? -1;
  if (setting === -1) return ceiling;
  if (ceiling === -1) return setting;
  return Math.min(setting, ceiling);
}

/** Devices a plan allows: the plan setting, never above the platform ceiling (-1 = the ceiling). Enterprise organizations may add extra devices. */
export function effectiveDevices(plan: Pick<TierConfig, 'max_assets' | 'platform'>) {
  const setting = plan.max_assets, ceiling = plan.platform?.device_ceiling ?? -1;
  if (setting === -1) return ceiling;
  if (ceiling === -1) return setting;
  return Math.min(setting, ceiling);
}

export function connections(value: string) {
  const names = value.split(',').map((v) => v.trim()).filter(Boolean).map((v) => CONNECTION_NAMES[v] ?? v);
  return names.length ? names.join(', ') : 'None';
}
